import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import { User } from '../../services/authService';
import { NovelService, Novel, Chapter } from '../../services/novelService';
import { trackAction } from '../../services/activityService';
import PageHeader from '../common/PageHeader';
import './PublicNovelReader.css';

interface PublicNovelReaderProps {
  user: User | null;
  onClose: () => void;
  onToggleSidebar: () => void;
}

const PublicNovelReader: React.FC<PublicNovelReaderProps> = ({ user, onClose, onToggleSidebar }) => {
  const { novelId } = useParams<{ novelId: string }>();
  const navigate = useNavigate();
  
  const [novel, setNovel] = useState<Novel | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentChapter, setCurrentChapter] = useState<Chapter | null>(null);

  useEffect(() => {
    if (currentChapter) {
      trackAction(user, 'NOVEL_READ', {
        novelId: novelId,
        chapterId: currentChapter.chapter_id,
        chapterTitle: currentChapter.title,
        publicView: true,
      });
    }
  }, [currentChapter, novelId, user]);

  useEffect(() => {
    if (novelId) {
      loadNovel();
    }
  }, [novelId]);

  const loadNovel = async () => {
    if (!novelId) return;

    try {
      setLoading(true);
      setError(null);
      const data = await NovelService.getPublicNovel(novelId);
      setNovel(data);
    } catch (err) {
      console.error('Error loading novel:', err);
      setError('Failed to load novel');
    } finally {
      setLoading(false);
    }
  };

  const handleChapterClick = (chapterId: string) => {
    navigate(`/novel/public/${novelId}/chapter/${chapterId}`);
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="public-novel-reader-container">
        <PageHeader
          onToggleSidebar={onToggleSidebar}
          centerContent={<h1 className="page-header-center-title">Loading...</h1>}
        />
        <div className="public-novel-reader-loading">Loading novel...</div>
      </div>
    );
  }

  if (error || !novel) {
    return (
      <div className="public-novel-reader-container">
        <PageHeader
          onToggleSidebar={onToggleSidebar}
          centerContent={<h1 className="page-header-center-title">Error</h1>}
        />
        <div className="public-novel-reader-error">
          {error || 'Novel not found'}
          <button 
            className="public-novel-reader-back-button"
            onClick={() => navigate('/novel')}
          >
            Back to Novels
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="public-novel-reader-container">
      <PageHeader
        onToggleSidebar={onToggleSidebar}
        leftActions={
          <button 
            className="public-novel-reader-back-btn"
            onClick={() => navigate('/novel')}
          >
            ← Back
          </button>
        }
        centerContent={
          <h1 className="page-header-center-title">{novel.title}</h1>
        }
      />

      <div className="public-novel-reader-content">
        <div className="public-novel-reader-info">
          <h1 className="public-novel-reader-title">{novel.title}</h1>
          
          {novel.author_name && (
            <p className="public-novel-reader-author">by {novel.author_name}</p>
          )}
          
          {novel.genre && (
            <span className="public-novel-reader-genre">{novel.genre}</span>
          )}

          <div className="public-novel-reader-meta">
            <span>{novel.chapters.length} {novel.chapters.length === 1 ? 'chapter' : 'chapters'}</span>
            <span className="public-novel-reader-separator">•</span>
            <span>Updated {formatDate(novel.updated_at)}</span>
          </div>

          {novel.metadata?.writingStyle && (
            <div className="public-novel-reader-description">
              <h3>About this novel</h3>
              <ReactMarkdown
                components={{
                  img: ({ node, ...props }) => (
                    <img 
                      {...props} 
                      className="public-novel-reader-image"
                      alt={props.alt || ''} 
                    />
                  )
                }}
              >
                {novel.metadata.writingStyle}
              </ReactMarkdown>
            </div>
          )}
        </div>

        <div className="public-novel-reader-chapters">
          <h2 className="public-novel-reader-chapters-title">Chapters</h2>
          
          {novel.chapters.length === 0 ? (
            <div className="public-novel-reader-empty">
              This novel has no chapters yet.
            </div>
          ) : (
            <div className="public-novel-reader-chapters-list">
              {novel.chapters.map((chapter, index) => (
                <button
                  key={chapter.chapter_id}
                  className="public-novel-reader-chapter-item"
                  onClick={() => handleChapterClick(chapter.chapter_id)}
                >
                  <span className="public-novel-reader-chapter-number">
                    Chapter {index + 1}
                  </span>
                  <span className="public-novel-reader-chapter-title">
                    {chapter.title}
                  </span>
                  <span className="public-novel-reader-chapter-arrow">→</span>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PublicNovelReader;

